import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { downloadInvoice } from '../utils/invoice';

export default function TrackingPage() {
  const { orderId } = useParams();
  const { orders, userEmail } = useStore();
  const order = orders.find(o => o.id === orderId);

  if (!order) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-slate-300 mb-4"></i>
          <h2 className="text-2xl font-bold mb-2">Order not found</h2>
          <p className="text-slate-500 mb-6">Please check your order ID</p>
          <Link to="/track-order" className="text-indigo-600 underline">Go back to tracking</Link>
        </div>
      </div>
    );
  }

  const statusSteps = [
    { key: 'Confirmed', label: 'Order Confirmed', icon: 'ri-checkbox-circle-line', date: order.createdAt },
    { key: 'Shipped', label: 'Shipped', icon: 'ri-truck-line', date: order.status === 'Shipped' ? 'In transit' : null },
    { key: 'Out for Delivery', label: 'Out for Delivery', icon: 'ri-bike-line', date: null },
    { key: 'Delivered', label: 'Delivered', icon: 'ri-home-smile-line', date: null }
  ];

  const currentIndex = statusSteps.findIndex(step => step.key === order.status);
  const progressPercent = ((currentIndex + 1) / statusSteps.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-fuchsia-600/10 rounded-3xl blur-3xl -z-10"></div>
        <h1 className="text-3xl md:text-4xl font-display font-bold bg-gradient-to-r from-indigo-700 to-fuchsia-700 bg-clip-text text-transparent">Track Your Order</h1>
        <p className="text-slate-500 mt-1">Order #{order.id.slice(-8)} • {order.trackingId}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Status Timeline */}
        <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold mb-6">Delivery Status</h2>
          
          {/* Progress Bar */}
          <div className="relative h-2 bg-slate-100 rounded-full mb-8 overflow-hidden">
            <div className="absolute h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 rounded-full transition-all duration-700" style={{ width: `${progressPercent}%` }}></div>
          </div>

          {/* Steps */}
          <div className="space-y-6">
            {statusSteps.map((step, idx) => {
              const isCompleted = idx <= currentIndex;
              const isCurrent = idx === currentIndex;
              return (
                <div key={step.key} className="flex gap-4 relative">
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-md' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <i className={`${step.icon} text-xl ${isCompleted ? 'text-white' : ''}`}></i>
                    </div>
                    {idx < statusSteps.length-1 && (
                      <div className={`absolute top-10 left-5 w-0.5 h-12 ${isCompleted ? 'bg-indigo-300' : 'bg-slate-200'}`}></div>
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <p className={`font-semibold ${isCurrent ? 'text-indigo-600' : isCompleted ? 'text-slate-800' : 'text-slate-400'}`}>
                      {step.label}
                      {isCurrent && <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Current</span>}
                    </p>
                    {step.date && <p className="text-xs text-slate-400 mt-0.5">{step.date}</p>}
                    {isCurrent && order.status === 'Shipped' && (
                      <p className="text-xs text-emerald-600 mt-1">Expected delivery: {order.deliveryEstimate}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Order Summary Card */}
        <div className="bg-gradient-to-br from-indigo-50 to-fuchsia-50 rounded-2xl p-6 border border-indigo-100 shadow-sm h-fit">
          <h3 className="font-bold text-lg mb-4">Order Summary</h3>
          <div className="mt-4">
             <button onClick={() => downloadInvoice(order, userEmail)} className="w-full py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 transition" >
             <i className="ri-download-line"></i> Download Invoice
             </button>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Total Amount</span>
              <span className="font-bold text-indigo-700">₹{order.total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Payment Method</span>
              <span className="capitalize">{order.paymentMethod}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Delivery Address</span>
              <span className="text-right text-sm">{order.address}</span>
            </div>
            <div className="pt-3 border-t">
              <p className="text-slate-600 mb-1">Items:</p>
              {order.items.slice(0, 2).map((item, i) => (
                <div key={i} className="flex justify-between text-xs py-1">
                  <span>{item.name} x{item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
              {order.items.length > 2 && <p className="text-xs text-slate-400">+{order.items.length-2} more</p>}
            </div>
          </div>

          {/* Mock map placeholder */}
          <div className="mt-6 bg-white rounded-xl p-3 border border-slate-200">
            <div className="bg-slate-100 rounded-lg h-24 flex items-center justify-center text-slate-400 text-sm">
              <i className="ri-map-pin-line mr-1"></i> Live map view (integrate later)
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">Last updated: Today, 10:30 AM</p>
          </div>

          <Link to="/order-history" className="block text-center mt-6 text-indigo-600 text-sm font-semibold hover:underline">
            ← Back to Orders
          </Link>
        </div>
      </div>
    </div>
  );
}